import { z } from "zod";
import type { ModelCatalogEntry } from "./domain.js";
import { ModelBackupChoiceSchema, modelChoiceListSchema } from "./domain.js";
import { Id, ThinkingLevelSchema } from "./ids.js";

/** Add purposes here; connections, persistence, pickers and backups stay shared. */
export const ModelRoleSchema = z.enum([
  "main",
  "approval",
  "video",
  "image",
  "documents",
  "embedding",
]);
export type ModelRole = z.infer<typeof ModelRoleSchema>;
export const ModelRoleChoiceSchema = ModelBackupChoiceSchema.extend({
  thinkingLevel: ThinkingLevelSchema.nullable().optional(),
  /** The connection this row runs on; omitted rows use the provider's connection or the deployment's. */
  credentialId: Id.optional(),
});
export type ModelRoleChoice = z.infer<typeof ModelRoleChoiceSchema>;
export const ModelRoleSettingsSchema = z.object({
  primary: ModelRoleChoiceSchema.nullable(),
  backups: modelChoiceListSchema(ModelRoleChoiceSchema),
});
export type ModelRoleSettings = z.infer<typeof ModelRoleSettingsSchema>;
export const ModelRoleUpdateSchema = ModelRoleSettingsSchema.partial();
/** A member's choices plus what the deployment runs when they chose no model. */
export const ModelRoleViewSchema = ModelRoleSettingsSchema.extend({
  deploymentPrimary: ModelRoleChoiceSchema.nullable().optional(),
  /** The deployment's model is included with the company's plan, metered to its credits. */
  included: z.boolean().optional(),
  /**
   * The company's plan runs this purpose on the deployment's models only; the member's own
   * choices and connections stay saved and unused.
   */
  includedPlan: z.boolean().optional(),
});
export type ModelRoleView = z.infer<typeof ModelRoleViewSchema>;

/** Purposes whose models speak their own protocol rather than chat. */
const MEDIA_ROLES = [
  "video",
  "image",
  "documents",
  "embedding",
] as const satisfies readonly ModelRole[];
const isMediaRole = (value: string | undefined): value is (typeof MEDIA_ROLES)[number] =>
  (MEDIA_ROLES as readonly string[]).includes(value ?? "");

export function modelSupportsRole(entry: ModelCatalogEntry, role: ModelRole): boolean {
  if (isMediaRole(role) || isMediaRole(entry.protocol)) return entry.protocol === role;
  return role === "approval" || entry.protocol !== "decisions";
}
